/* eslint-disable max-len */
import { useEffect, useState } from "react";
import {
  Show,
  SimpleShowLayout,
  ReferenceField,
  TextField,
  useTranslate,
  List,
  FunctionField,
  useShowContext,
  useDataProvider,
  useRefresh,
  useNotify
} from "react-admin";
import { useParams } from "react-router-dom";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  AppointmentDetailsIcon,
  ArtistSpecifiedIcon,
  EditButtonIcon
} from "assets/Icons";
import CustomHeaderActions from "./CustomHeaderActions";
import { Link } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { muiColors } from "themes/colors";
import {
  Avatar,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery
} from "@mui/material";
import CustomDataGrid from "./CustomDataGrid";
import AppointmentDetailsCustomButtons from "./AppointmentDetailsCustomButtons";
import UserAvatarAndName from "./UserAvatarAndName";
import AppointmentShowLog from "./AppointmentShowLog";
import { IServiceAddOn } from "types";
import CancellationModal from "./CancellationModal";
import { getZonedDate } from "../utils/helpers";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import * as Axios from "providers/axios";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DateTimePicker, DesktopDateTimePicker } from "@mui/x-date-pickers";
import * as User from "providers/user";
import LimitedCustomerPreviewModal from "./LimitedCustomerPreviewModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const AppointmentOrderField = ({
  label,
  rescheduledOrder,
  setRescheduledOrder,
  isCompanyLimited
}: {
  label: string;
  rescheduledOrder: any;
  setRescheduledOrder: any;
  isCompanyLimited: boolean | undefined;
}) => {
  const { record } = useShowContext();
  const dataProvider = useDataProvider();

  const findAppointmentWithRelatedOrder = async () => {
    let currentAppointmentId = record?.id;
    if (!record?.Order?.id) {
      while (currentAppointmentId !== null) {
        try {
          const res = await dataProvider.getList("Appointment", {
            pagination: { page: 1, perPage: 1 },
            sort: { field: "id", order: "ASC" },
            filter: { rescheduled_from_id: currentAppointmentId }
          });
          if (res?.data?.[0]?.Order?.id) {
            currentAppointmentId = null;
            setRescheduledOrder(res?.data?.[0]?.Order);
          } else {
            currentAppointmentId = res?.data?.[0]?.id;
          }
        } catch (err) {
          currentAppointmentId = null;
        }
      }
    }
  };

  useEffect(() => {
    findAppointmentWithRelatedOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (record?.id && rescheduledOrder?.id) {
    const amountPaidByUserNotIncludingTipsFromRescheduledOrder =
      rescheduledOrder?.transactions
        ?.filter((item: any) => item?.status === "SUCCESS")
        ?.reduce((acc: number, curr: any) => {
          acc += curr?.amount ?? 0;
          acc -= curr?.payment_fees ?? 0;
          acc -= curr?.total_tip_amount ?? 0;
          return acc;
        }, 0);
    return (
      <>
        {isCompanyLimited ? (
          <div>
            <span className="text-[16px] font-[400] font-['Inter'] first-letter:capitalize">
              {rescheduledOrder.payment_status
                ?.replaceAll("_", " ")
                ?.toLowerCase()}
              , $
              {Number(
                (
                  amountPaidByUserNotIncludingTipsFromRescheduledOrder || 0
                )?.toFixed(2)
              )}{" "}
              of ${Number((record?.full_amount || 0)?.toFixed(2))}
            </span>
          </div>
        ) : (
          <Link
            data-cy="order-redirect-button"
            to={`/dashboard/Appointment-order/${rescheduledOrder.id}/show`}
          >
            <span className="text-[16px] text-[#16667A] font-[400] font-['Inter'] first-letter:capitalize">
              {rescheduledOrder.payment_status
                ?.replaceAll("_", " ")
                ?.toLowerCase()}
              , $
              {Number(
                (
                  amountPaidByUserNotIncludingTipsFromRescheduledOrder || 0
                )?.toFixed(2)
              )}{" "}
              of ${Number((record?.full_amount || 0)?.toFixed(2))}
            </span>
          </Link>
        )}
      </>
    );
  }
  if (record?.Order?.id) {
    const amountPaidByUserNotIncludingTips = record?.Order?.transactions
      ?.filter((item: any) => item?.status === "SUCCESS")
      ?.reduce((acc: number, curr: any) => {
        acc += curr?.amount ?? 0;
        acc -= curr?.payment_fees ?? 0;
        acc -= curr?.total_tip_amount ?? 0;
        return acc;
      }, 0);

    return (
      <>
        {isCompanyLimited ? (
          <div>
            <span className="text-[16px] font-[400] font-['Inter'] first-letter:capitalize">
              {record.Order.payment_status?.replaceAll("_", " ")?.toLowerCase()}
              , ${Number((amountPaidByUserNotIncludingTips || 0)?.toFixed(2))}{" "}
              of ${Number((record?.Order?.full_amount || 0)?.toFixed(2))}
            </span>
          </div>
        ) : (
          <Link
            data-cy="order-redirect-button"
            to={`/dashboard/Appointment-order/${record.Order.id}/show`}
          >
            <span className="text-[16px] text-[#16667A] font-[400] font-['Inter'] first-letter:capitalize">
              {record.Order.payment_status?.replaceAll("_", " ")?.toLowerCase()}
              , ${Number((amountPaidByUserNotIncludingTips || 0)?.toFixed(2))}{" "}
              of ${Number((record?.Order?.full_amount || 0)?.toFixed(2))}
            </span>
          </Link>
        )}
      </>
    );
  }
  return null;
};

export default function AppointmentShow() {
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const [limitedCustomerPreview, setLimitedCustomerPreview] =
    useState<any>(null);
  const { id } = useParams<{ id: string }>();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const [rescheduledOrder, setRescheduledOrder] = useState<any>(null);
  const [isDateEditMode, setIsDateEditMode] = useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Show
      actions={
        <CustomHeaderActions title={translate("pagenames.appointment")} />
      }
      sx={{
        "& .RaShow-card": {
          backgroundColor: muiColors.bg.default
        },
        "& .RaDatagrid-headerCell": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          paddingTop: "12px",
          paddingBottom: "12px"
        },
        "& .RaDatagrid-rowCell": {
          borderBottom: "none"
        }
      }}
    >
      <div className="w-full grid xl:grid-cols-4 2xl:grid-cols-3 gap-4">
        <div className="h-min xl:col-span-3 2xl:col-span-2 bg-mono-primaryLightBgColor rounded-[8px]">
          <SimpleShowLayout>
            <ShowAndEditDetails
              title={translate("appointment.appointment_details")}
              view="show"
              icon={<AppointmentDetailsIcon />}
              customButtons={
                <AppointmentDetailsCustomButtons
                  setCancelModalAppointmentId={setCancelModalAppointmentId}
                  rescheduledOrderId={rescheduledOrder?.id}
                />
              }
              marginBottom="0px"
            />
            {/* <Buttons /> */}
            <ReferenceField
              source="location_id"
              reference="Location"
              label={translate("appointment.location") + ":"}
            >
              <FunctionField
                render={(record: any) => {
                  if (record.nickname) {
                    return <TextField source="nickname" />;
                  } else {
                    return <TextField source="name" />;
                  }
                }}
              />
            </ReferenceField>
            <AppointmentOrderField
              label={translate("appointment.order") + ":"}
              rescheduledOrder={rescheduledOrder}
              setRescheduledOrder={setRescheduledOrder}
              isCompanyLimited={permissions?.isCompanyLimited}
            />
            <FunctionField
              label={
                isDateEditMode
                  ? " "
                  : translate("appointment.start_end_time") + ":"
              }
              render={(record: any) => (
                <DateAndTime
                  record={record}
                  isDateEditMode={isDateEditMode}
                  setIsDateEditMode={setIsDateEditMode}
                />
              )}
            />
            <List
              resource="AppointmentService"
              filter={{
                appointment_id: id
              }}
              sort={{ field: "start_time", order: "ASC" }}
              actions={<div className="h-0" />}
              sx={{
                marginTop: "4px",
                "& .RaList-main > div": {
                  minHeight: "0px"
                },
                "& .MuiTablePagination-root": {
                  display: "none"
                }
              }}
            >
              <CustomDataGrid
                rowClick={false}
                hover={false}
                {...(!isSmall
                  ? {
                      sx: {
                        borderRadius: "0px",
                        ".RaDatagrid-headerCell": {
                          backgroundColor: "transparent",
                          fontSize: "14px",
                          "@media screen and (max-width:1280px)": {
                            fontSize: "12px"
                          }
                        },
                        ".RaDatagrid-rowCell span": {
                          fontSize: "14px",
                          "@media screen and (max-width:1280px)": {
                            fontSize: "12px"
                          }
                        }
                      }
                    }
                  : {})}
              >
                <FunctionField
                  label={translate("appointment.customer")}
                  data-label={translate("appointment.customer") + ":"}
                  sortBy="person_number"
                  sx={{ marginTop: "6px" }}
                  render={(record: any) => (
                    <div className="flex items-center gap-[16px]">
                      {record?.person_number === 0 ? (
                        <>
                          {!permissions?.isCompanyLimited ? (
                            <UserAvatarAndName
                              label={translate("common.user")}
                              fromAppointmentList
                              nameStyles="line-clamp-1 break-all text-[14px] sm:text-[12px] xl:text-[14px]"
                            />
                          ) : (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setLimitedCustomerPreview(record?.user);
                              }}
                              aria-hidden
                              className="min-h-[28px] flex items-center cursor-pointer"
                            >
                              <span className="text-[#16667A]">
                                {`${record?.user?.first_name} ${record?.user?.last_name}`}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <Avatar
                            sx={{
                              width: "32px",
                              height: "32px",
                              "@media screen and (max-width:800px)": {
                                width: "24px",
                                height: "24px",
                                fontSize: "16px"
                              }
                            }}
                          />
                          <span className="!text-[12px] xl:!text-[14px]">{`Person ${
                            record?.person_number + 1
                          }`}</span>
                        </>
                      )}
                    </div>
                  )}
                />
                <FunctionField
                  label={translate("appointment.service")}
                  data-label={translate("appointment.service") + ":"}
                  sortBy="service.name"
                  render={(record: any) => {
                    return (
                      <div className="flex flex-wrap items-center">
                        <Link
                          to={`/dashboard/Service/${record?.service?.id}/show`}
                          className="flex items-center text-mono-link whitespace-nowrap"
                          style={{ fontSize: "inherit" }}
                        >
                          {record?.service?.name}
                        </Link>
                        {!!record?.add_ons.length && (
                          <span className="text-[#16667A] whitespace-normal break-all">
                            {" "}
                            (
                            {record?.add_ons?.map(
                              (addOn: IServiceAddOn, index: number) => (
                                <span>
                                  <ReferenceField
                                    source="service_id"
                                    reference="AddOns"
                                    record={addOn}
                                    key={addOn.id}
                                    link="show"
                                  >
                                    <TextField
                                      source="name"
                                      sx={{
                                        marginTop: "20px",
                                        color: "#16667A",
                                        whiteSpace: "nowrap"
                                      }}
                                    />
                                  </ReferenceField>
                                  {index < record?.add_ons?.length - 1
                                    ? ", "
                                    : ""}
                                </span>
                              )
                            )}
                            )
                          </span>
                        )}
                      </div>
                    );
                  }}
                />
                <FunctionField
                  label={translate("appointment.artists")}
                  data-label={translate("appointment.artists") + ":"}
                  sortBy="artist.user.first_name.sort"
                  render={(record: any) => {
                    return (
                      <div className="flex gap-1 items-center">
                        <Link
                          to={`/dashboard/Artist/${record?.artist?.id}/show`}
                          className="flex items-center text-mono-link"
                          style={{ fontSize: "inherit" }}
                        >
                          <p className="line-clamp-1 break-all min-w-[100px] text-[14px] sm:text-[12px] xl:text-[14px]">
                            {record?.artist?.user?.first_name +
                              " " +
                              record?.artist?.user?.last_name}
                          </p>
                        </Link>
                        {record?.appointment?.is_artist_specified && (
                          <ClickAwayListener
                            onClickAway={() => setIsTooltipOpen(false)}
                          >
                            <Tooltip
                              id="is-artist-specified-tooltip"
                              onClose={() => setIsTooltipOpen(false)}
                              open={!!isTooltipOpen}
                              placement="top"
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                              title={translate(
                                "appointment.this_artist_was_specified_by_customer"
                              )}
                              // @ts-ignore
                              // slotProps={{
                              //   popper: {
                              //     disablePortal: true
                              //   }
                              // }}
                            >
                              <div
                                className="cursor-pointer"
                                onClick={() => {
                                  setIsTooltipOpen(true);
                                  setTimeout(() => {
                                    setIsTooltipOpen(false);
                                  }, 3000);
                                }}
                                aria-hidden
                              >
                                <ArtistSpecifiedIcon width="20" height="20" />
                              </div>
                            </Tooltip>
                          </ClickAwayListener>
                        )}
                      </div>
                    );
                  }}
                />
                <FunctionField
                  label={translate("appointment.start_time")}
                  data-label={translate("appointment.start_time") + ":"}
                  source="start_time"
                  textAlign="right"
                  align="right"
                  render={(record: any) => (
                    <div className="flex justify-end items-center">
                      {dayjs(
                        getZonedDate(
                          record?.start_time,
                          record?.appointment?.location?.timezone
                        )
                      ).format("M/D/YYYY, hh:mmA")}
                    </div>
                  )}
                />
                <FunctionField
                  label={translate("appointment.end_time")}
                  data-label={translate("appointment.end_time") + ":"}
                  textAlign="right"
                  source="end_time"
                  render={(record: any) => (
                    <div className="flex justify-end items-center">
                      {dayjs(
                        getZonedDate(
                          record?.end_time,
                          record?.appointment?.location?.timezone
                        )
                      ).format("M/D/YYYY, hh:mmA")}
                    </div>
                  )}
                />
                {isSmall && <div className="emptyEl" />}
              </CustomDataGrid>
            </List>
          </SimpleShowLayout>
        </div>
        <AppointmentShowLog appointmentId={id} />
      </div>
      {cancelModalAppointmentId && (
        <CancellationModal
          cancelModalAppointmentId={cancelModalAppointmentId}
          setCancelModalAppointmentId={setCancelModalAppointmentId}
        />
      )}
      {limitedCustomerPreview?.id && (
        <LimitedCustomerPreviewModal
          user={limitedCustomerPreview}
          onClose={() => setLimitedCustomerPreview(null)}
        />
      )}
    </Show>
  );
}

const DateAndTime = ({ record, isDateEditMode, setIsDateEditMode }: any) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { api } = Axios.useAxios();
  const { permissions } = User.useUser();
  const [startDateAndTimeState, setStartDateAndTimeState] =
    useState<Dayjs | null>(dayjs(record?.start_time) || null);
  const [isEditing, setIsEditing] = useState(false);

  const sortedAppointmentServices = [...record?.appointment_services].sort(
    (a, b) => {
      if (a.person_number !== b.person_number) {
        return a.person_number - b.person_number;
      }
      return (
        (new Date(a.start_time) as unknown as number) -
        (new Date(b.start_time) as unknown as number)
      );
    }
  );

  const diffBetweenAppointmentStartTime = dayjs(startDateAndTimeState).diff(
    dayjs(record.start_time),
    "minute",
    true
  );
  const appointmentServices = !diffBetweenAppointmentStartTime
    ? []
    : sortedAppointmentServices?.map((item: any) => {
        return {
          id: item?.id,
          start_time: dayjs(item?.start_time)?.isValid()
            ? diffBetweenAppointmentStartTime > 0
              ? dayjs(item.start_time)
                  .add(diffBetweenAppointmentStartTime, "minute")
                  ?.toISOString()
              : dayjs(item.start_time)
                  .subtract(Math.abs(diffBetweenAppointmentStartTime), "minute")
                  ?.toISOString()
            : null,
          end_time: dayjs(item?.end_time)?.isValid()
            ? diffBetweenAppointmentStartTime > 0
              ? dayjs(item.end_time)
                  .add(diffBetweenAppointmentStartTime, "minute")
                  ?.toISOString()
              : dayjs(item.end_time)
                  .subtract(Math.abs(diffBetweenAppointmentStartTime), "minute")
                  ?.toISOString()
            : null
        };
      });

  const latestAppointmentServicesDate = !diffBetweenAppointmentStartTime
    ? undefined
    : appointmentServices?.reduce((latest: any, current: any) => {
        return new Date(current.end_time) > new Date(latest.end_time)
          ? current
          : latest;
      })?.end_time;

  const payload = {
    appointmentId: record?.id,
    startTime: startDateAndTimeState?.isValid()
      ? startDateAndTimeState?.toISOString()
      : null,
    endTime: latestAppointmentServicesDate,
    appointmentServices: appointmentServices,
    companyId: permissions.companyId
  };

  const isMovable =
    payload?.appointmentId &&
    payload?.startTime &&
    payload?.endTime &&
    payload?.appointmentServices?.length > 0 &&
    payload?.companyId
      ? true
      : false;

  const rescheduleAppointmentWithoutCheckingAvailability = async () => {
    if (!isMovable) return;
    setIsEditing(true);
    try {
      await api.post("/v1/appointments/move-appointment", payload);
      refresh();
      setIsEditing(false);
      setIsDateEditMode(false);
    } catch (e) {
      notify(
        "Error occurred during editing date and time, please try again later",
        {
          type: "error",
          undoable: false,
          anchorOrigin: { vertical: "bottom", horizontal: "center" }
        }
      );
      setIsEditing(false);
    }
  };

  return (
    <>
      {isDateEditMode ? (
        <div className={`flex items-center gap-[8px] mt-[2px] max-w-[500px]`}>
          <div className="relative w-full">
            <DesktopDateTimePicker
              label={translate("appointment.start_date_and_time")}
              value={startDateAndTimeState}
              onChange={(newValue) => setStartDateAndTimeState(newValue)}
              timezone={record?.location?.timezone}
              sx={{
                width: "100%",
                "& input": {
                  height: "27px !important"
                },
                "& .MuiIconButton-root": {
                  "& svg": {
                    fill: "#8c8c8c"
                  }
                }
              }}
              slotProps={{ textField: { size: "small" } }}
            />
            <DateTimePicker
              label={translate("appointment.start_date_and_time")}
              value={startDateAndTimeState}
              onChange={(newValue) => setStartDateAndTimeState(newValue)}
              timezone={record?.location?.timezone}
              sx={{
                position: "absolute",
                top: "0px",
                right: "0px",
                width: "44px !important",
                height: "52px !important",
                "& .MuiInputLabel-root": { display: "none" },
                "& .MuiFilledInput-root": {
                  border: "none !important",
                  paddingRight: "24px !important",
                  backgroundColor: "transparent !important"
                },
                "& .MuiFilledInput-root:after": {
                  border: "none !important"
                },
                "& .MuiFilledInput-root.Mui-error:after": {
                  border: "none !important"
                },
                "& .MuiFilledInput-root.Mui-focused": {
                  backgroundColor: "transparent"
                },
                "& .MuiFilledInput-input": {
                  height: "28px",
                  marginRight: "-8px",
                  paddingLeft: "0px !important",
                  visibility: "hidden"
                },
                "& .MuiIconButton-root": {
                  marginRight: "0px !important",
                  "& svg": {
                    fill: "#8c8c8c"
                  }
                }
              }}
              slotProps={{ textField: { size: "small" } }}
            />
          </div>
          <IconButton
            onClick={() => {
              setStartDateAndTimeState(dayjs(record?.start_time) || null);
              setIsDateEditMode(false);
            }}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={rescheduleAppointmentWithoutCheckingAvailability}
            disabled={
              diffBetweenAppointmentStartTime === 0 ||
              !startDateAndTimeState?.isValid() ||
              isEditing
            }
            sx={{
              color: muiColors.primary,
              ".Mui-disabled": { color: "red" }
            }}
          >
            {isEditing ? (
              <CircularProgress style={{ width: 20, height: 20 }} />
            ) : (
              <CheckIcon />
            )}
          </IconButton>
        </div>
      ) : (
        <div className={`flex items-center gap-[8px] mt-[2px]`}>
          <div className="text-[16px] text-[rgba(0,0,0,0.87)] font-['Inter']">
            {dayjs(
              getZonedDate(record?.start_time, record?.location?.timezone)
            ).format("hh:mm A")}{" "}
            -{" "}
            {dayjs(
              getZonedDate(record?.end_time, record?.location?.timezone)
            ).format("hh:mm A")}
            ,{" "}
            {dayjs().isSame(
              dayjs(
                getZonedDate(record?.start_time, record?.location?.timezone)
              ).format("MM/DD/YYYY"),
              "day"
            )
              ? "Today"
              : dayjs(
                  getZonedDate(record?.start_time, record?.location?.timezone)
                ).format("MM/DD/YYYY")}
          </div>
          {!record?.rescheduled_at &&
            !permissions.isArtist &&
            !permissions.isCompanyLimited && (
              <Tooltip title={translate(`common.edit`)} placement="top">
                <IconButton
                  onClick={() => {
                    setIsDateEditMode(true);
                  }}
                >
                  <EditButtonIcon />
                </IconButton>
              </Tooltip>
            )}
        </div>
      )}
    </>
  );
};
